// Words-per-minute (WPM) distributions for SEO calculator pages.
//
// Source: the July 2026 hook study at marketing/growth/tooling/hook-study/
// (classified.jsonl for per-video WPM, channel_genres.json for genre labels).
// Every number in this file is computed from that data by
// compute_wpm_stats.py, never hand-edited. Genre labels were assigned per
// channel by classify_july_channels.py, which reuses the frozen classifier
// from marketing/growth/tooling/small-channel-study/classify_genre.py.
// Only genres with at least 20 videos get their own row; smaller genres and
// channels labeled "other" are counted in the overall distribution only.

export interface WpmDistribution {
  n: number
  p25: number
  median: number
  p75: number
}

export interface GenreWpmDistribution extends WpmDistribution {
  label: string
}

// All 349 videos with a valid WPM value.
export const overallWpm: WpmDistribution = {
  n: 349,
  p25: 160,
  median: 181,
  p75: 201,
}

// Genres with n >= 20 videos. Videos inherit their channel's genre.
export const wpmByGenre: Record<string, GenreWpmDistribution> = {
  education: {
    label: 'Education',
    n: 89,
    p25: 153,
    median: 179,
    p75: 204,
  },
  finance: {
    label: 'Finance',
    n: 40,
    p25: 166,
    median: 190,
    p75: 224,
  },
}

// Median WPM for outlier hits (views far above channel median) vs flops
// (views far below channel median). The difference is negligible: delivery
// pace alone does not separate hits from flops in this dataset.
export const wpmHitsVsFlops = {
  hits: { n: 177, median: 181 },
  flops: { n: 172, median: 180 },
}

export const wpmMethodology =
  'Words per minute computed as total spoken transcript words divided by video length, across 349 YouTube videos from 36 channels, collected in July 2026. Genres were assigned per channel by the same frozen automated classifier used in our preregistered small-channel study. These numbers describe association only, not causation.'
